import { ignoreAllRejects, generateCatchHandler, CatchHandler } from './Error';
import { assert } from './Debug';
import { AsyncFunction } from './Types';

import utils = egr.wcf.utils;

export interface PromiseCallbacks<T = void> {
    promise: Promise<T>;
    resolve: (data: T) => void;
    reject: (error: Error) => void;
}

export function resolveImmediately(): Promise<void> {
    return Promise.resolve();
}

export async function getLockedTaskQueue(): Promise<{queue: utils.async.TaskQueue, unlock: () => void}> {
    const lock: utils.async.Lock = new utils.async.Lock();
    const queue: utils.async.TaskQueue = new utils.async.TaskQueue(lock);
    const context: utils.async.LockContext = await lock.acquireLock();

    assert(lock.isLocked === true, 'lock should be locked');
    assert(queue.hasPendingTasks === true, 'locked queue should have pending tasks');

    return {
        queue: queue,
        unlock: context.unlock
    };
}

export function getPromiseCallbacks<T = void>(): PromiseCallbacks<T> {
    const callback: PromiseCallbacks<T> = {} as PromiseCallbacks<T>;

    callback.promise = new Promise<T>((resolve: (data: T) => void, reject: (error: Error) => void) => {
        callback.resolve = resolve;
        callback.reject = reject;
    });

    return callback;
}

export function timeout(delay: number = 0): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (error: Error) => void): void => {
        window.setTimeout(resolve, delay);
    });
}

export class TaskCancelledError extends utils.CustomError {
    public constructor(message?: string) {
        super(message);

        Object.setPrototypeOf(this, TaskCancelledError.prototype);
    }
}

const defaultErrorGenerator: () => never = (): never => {
    throw new TaskCancelledError('Task was cancelled');
};

/**
 * Note: this ignores only the default `TaskCancelledError` of errors which
 * inherit from `TaskCancelledError`
 */
export const ignoreTaskCancelErrors: CatchHandler = generateCatchHandler([TaskCancelledError]);

export class Queue {
    private queueEnd: Promise<void> = Promise.resolve();
    private cancelErrorGenerator: (() => never) | null = null;

    public async add<T = void>(callback: () => Promise<T>): Promise<T> {
        const queueEntry: Promise<void> = this.queueEnd;

        const {promise, resolve} = getPromiseCallbacks<void>();
        this.queueEnd = promise;

        await queueEntry;

        try {
            if (this.cancelErrorGenerator) {
                this.cancelErrorGenerator();
            }

            const value: T = await callback();

            if (this.cancelErrorGenerator) {
                this.cancelErrorGenerator();
            }

            return value;
        } finally {
            window.setTimeout(resolve, 0);
        }
    }

    /**
     * This will call the `errorGenerator` function before and after each
     * task was executed
     *
     * Note: the task, which is already running, will only be cancled after
     * its termination.
     *
     * @param errorGenerator a function witch throws an Error on every call
     */
    public async cancle(errorGenerator: () => never = defaultErrorGenerator): Promise<void> {
        this.cancelErrorGenerator = errorGenerator;

        try {
            await this.add((): Promise<void> => {
                return Promise.resolve();
            });
        } catch (error) {
            // do nothing
        } finally {
            this.cancelErrorGenerator = null;
        }
    }
}

export class ThrottledQueue extends Queue {
    protected timeoutGenerator: () => Promise<void>;

    public constructor(delay: number) {
        super();

        this.timeoutGenerator = (): Promise<void> => {
            return timeout(delay);
        };
    }

    public async add<T = void>(callback: () => Promise<T>): Promise<T> {
        const task: Promise<T> = super.add<T>(callback);
        super.add(this.timeoutGenerator).catch(ignoreAllRejects);

        return task;
    }
}

export class Bouncer {
    protected pending: boolean = false;
    protected running: boolean = false;
    protected queue: ThrottledQueue;

    public constructor(protected callback: () => Promise<void>, delay: number) {
        this.queue = new ThrottledQueue(delay);
    }

    public trigger: () => void = (): void => {
        if (!this.running) {
            void this.startTask(); // note do not await
        } else if (!this.pending) {
            this.pending = true;
        }
    };

    protected startTask: () => Promise<void> = (): Promise<void> => {
        this.pending = false;
        this.running = true;

        const task: Promise<void> = this.queue.add<void>(this.callback);

        // Note: it is important that the `endTask` is added immediately after the
        // `startTask`
        void this.queue.add(this.endTask);

        return task;
    };

    protected endTask: () => Promise<void> = (): Promise<void> => {
        this.running = false;

        if (this.pending) {
            window.setTimeout(this.startTask);
        }

        return Promise.resolve();
    };
}

/**
 * Should only be used when absolutely necessary, as it removes the advantages of AsyncIterableIterator.
 * (A valid use case is, for example, sorting the results.)
 */
export async function AsyncIteratorToArray<T>(iterator: AsyncIterableIterator<T>): Promise<Array<T>> {
    const result: Array<T> = [];
    for await (const value of iterator) {
        result.push(value);
    }

    return result;
}

export function animationFrame(cb: VoidFunction): Promise<void> {
    return new Promise((resolve: VoidFunction): void => {
        requestAnimationFrame((): void => {
            window.setTimeout(resolve);
            cb();
        });
    });
}

export interface EventData<P, O> {
    parameter: P | undefined;
    owner: O;
}

export function awaitOnce<P, O>(event: utils.ListenableEvent<P, O> | utils.AsyncListenableEvent<P, O>): Promise<EventData<P, O>> {
    return new Promise<EventData<P, O>>((resolve: (data: EventData<P, O>) => void): void => {
        if (event instanceof utils.ListenableEvent) {
            const callback: utils.EventListener<P, O> = (parameter: P | undefined, owner: O): void => {
                event.removeListener(callback);

                resolve({
                    parameter,
                    owner
                });
            };

            event.addListener(callback);
        } else {
            const callback: utils.AsyncEventListener<P, O> = (parameter: P | undefined, owner: O): Promise<void> => {
                event.removeListener(callback);

                resolve({
                    parameter,
                    owner
                });

                // FixMe: ...
                return timeout(0);
            };

            event.addListener(callback);
        }
    });
}

export async function rejectValue<T>(promise: Promise<T>, fallback: T): Promise<T> {
    try {
        return await promise;
    } catch {
        return fallback;
    }
}

export async function fallbackAction<T = void>(
    action: AsyncFunction<T>,
    fallback: AsyncFunction<T>,
    fallbackDelay: number,
    failTimeout?: number
): Promise<T> {
    return new Promise<T>((resolve: (value: T) => void, reject: (error: Error) => void): void => {
        let finished: boolean = false;
        let fallbackStarted: boolean = false;
        let finalReject: boolean = false;

        const callReject: (error: Error) => void = (error: Error): void => {
            if (finalReject && !finished) {
                finished = true;
                reject(error);
            } else {
                finalReject = true;
            }
        };

        const callResolve: (value: T) => void = (value: T): void => {
            if (!finished) {
                finished = true;
                resolve(value);
            }
        };

        const startFallback: VoidFunction = (): void => {
            if (!fallbackStarted && !finished) {
                fallbackStarted = true;

                fallback().then(callResolve).catch(callReject);
            }
        };

        action().then(callResolve).catch((error: Error): void => {
            startFallback();
            callReject(error);
        });

        void timeout(fallbackDelay).then(startFallback);

        if (failTimeout != null) {
            void timeout(failTimeout).then((): void => {
                finalReject = true;
                callReject(new Error('Timeout reached'));
            });
        }
    });
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/utils/Promise.ts